// src/modules/app-views/leads/pages/Init/modals/LeadsParametersModal/modals/AddDataSourceModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo } from 'react';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import step1Fn from './Step1';
import step2Fn from './Step2';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AddDataSourceModal = ({ show, setShow, editingRecord, onSuccess }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isEditing = !!editingRecord;

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = useCallback(async ({ form, setLoading }) => {
    const values = form.getFieldsValue(true);
    setLoading(true);

    try {
      const fm = values.fieldMapping || {};
      // `token` is user-provided (generated in the modal) and is the shared key
      // that links this source to leads in Firestore. The SAME token can be added
      // in many subscriptions so all of them receive that token's leads, so the
      // backend index is unique per { token, subscription }, NOT globally.
      const payload = {
        name: (values.name || '').trim(),
        description: (values.description || '').trim() || null,
        url: (values.url || '').trim() || null,
        token: (values.token || '').trim(),
        interestTemplate: (values.interestTemplate || '').trim() || null,
        fieldMapping: {
          uniqueKey: (fm.uniqueKey || '').trim() || null,
          name:      (fm.name || '').trim() || null,
          phone:     (fm.phone || '').trim() || null,
          email:     (fm.email || '').trim() || null,
        },
      };

      let savedDoc;

      if (isEditing) {
        const url = API.DATA_SOURCES.URL.replace(':_id?', editingRecord._id);
        const [doc] = await axiosService.put({ url, data: payload });
        savedDoc = doc || { ...editingRecord, ...payload };
      } else {
        const url = API.DATA_SOURCES.URL.replace(':_id?', '');
        const [doc] = await axiosService.post({ url, data: payload });
        savedDoc = doc;
      }

      appService.message({ flag: 's', text: 'saved_successfully' });
      onSuccess?.(savedDoc, isEditing);
    } catch (error) {
      console.error('[AddDataSourceModal] Save error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
      return false; // keep modal open on error
    } finally {
      setLoading(false);
    }
  }, [isEditing, editingRecord, onSuccess]);

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const initialValues = useMemo(() => {
    // Default fieldMapping values - standard field names wrapped in template syntax
    const defaultFieldMapping = {
      uniqueKey: '{uniqueKey}',
      name:      '{name}',
      phone:     '{phone}',
      email:     '{email}',
    };

    if (!editingRecord) return { fieldMapping: defaultFieldMapping };

    const rec = editingRecord;
    return {
      name:             rec.name || '',
      description:      rec.description || '',
      url:              rec.url || '',
      token:            rec.token || '',
      interestTemplate: rec.interestTemplate || '',
      fieldMapping: {
        uniqueKey: rec.fieldMapping?.uniqueKey || defaultFieldMapping.uniqueKey,
        name:      rec.fieldMapping?.name      || defaultFieldMapping.name,
        phone:     rec.fieldMapping?.phone     || defaultFieldMapping.phone,
        email:     rec.fieldMapping?.email     || defaultFieldMapping.email,
      },
    };
  }, [editingRecord]);

  // Block advancing to Step 2 when another data source in THIS subscription
  // already uses the same token. (The same token across DIFFERENT subscriptions
  // is allowed by design, so this only checks within the current tenant.)
  const guardDuplicateToken = useCallback(
    async ({ form, setLoading }) => {
      const token = (form.getFieldValue('token') || '').trim();
      if (!token) return true; // empties handled by required-field validation

      try {
        setLoading(true);
        const baseUrl = API.DATA_SOURCES.URL.replace('/:_id?', '');
        const [matches] = await axiosService.assertFieldNotExists(baseUrl, 'token', token);
        const list = Array.isArray(matches) ? matches : matches ? [matches] : [];
        const conflict = list.some(
          d => d?._id && (!editingRecord || String(d._id) !== String(editingRecord._id))
        );
        if (conflict) {
          appService.message({ flag: 'w', text: 'data_source_token_exists' });
          return false; // stay on Step 1
        }
        return true;
      } catch {
        // Lookup failed -> don't hard-block; the per-subscription unique index is the safety net
        return true;
      } finally {
        setLoading(false);
      }
    },
    [editingRecord]
  );

  const steps = useMemo(() => [
    {
      ...step1Fn({ isEditing }),
      onEnter: ({ goFocus }) => goFocus('name'),
      onNext: guardDuplicateToken,
    },
    step2Fn(),
  ], [isEditing, guardDuplicateToken]);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={isEditing ? 'edit_data_source' : 'add_data_source'}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={520}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AddDataSourceModal;
